/**
 * Pure business logic for site plan lot data.
 *
 * Covers lot status key mapping, status index building, layer ordering,
 * and anchor-path ID resolution.
 * No DOM, no map API, no shared mutable state — all exports are deterministic.
 *
 * @module searchResultsMap/services/sitePlanLots
 */

import LotStatus from "../enums/lotStatus";

/**
 * Maps a raw API lot status string to the CSS class-key used in lotClasses.
 *
 * - Sold        → 'sold'
 * - Unreleased  → 'unreleased'
 * - Inventory, QuickMoveIn, or any other recognised status → 'active'
 * - null / unknown → 'inactive'
 *
 */
export function lotStatusToKey(status) {
  if (!status) {
    return 'inactive';
  }
  if (status === LotStatus.Sold.id) {
    return 'sold';
  }
  if (status === LotStatus.Unreleased.id) {
    return 'unreleased';
  }
  return LotStatus.fromKey(status) ? 'active' : 'inactive';
}

/**
 * Builds a Map<lotId, lot> from the SitePlanLots array for O(1) status lookups.
 *
 */
export function buildLotStatusIndex(lots) {
  const index = new Map();
  if (!lots) {
    return index;
  }
  lots.forEach((lot) => {
    if (lot.id) {
      index.set(lot.id, lot);
    }
  });
  return index;
}

/**
 * Returns a new array of layers sorted ascending by their order property.
 *
 */
export function sortLayersByOrder(layers) {
  return layers.slice().sort((a, b) => a.order - b.order);
}
